import {
  REPORTS_PAGE_LIMIT,
  REPORTS_PAGE_SIZE_OPTIONS,
} from '@/screens/Reports/reportsScreen.constants';
import { ReportResponseDto } from '@api/WhisApi/generated';
import { Button } from '@components/Button/Button';
import { Pagination } from '@components/Pagination/Pagination';
import ReportCard from '@components/ReportList/ReportCard/ReportCard';
import { getReportSortOptions } from '@components/ReportList/reportList.constants';
import { ReportSummary } from '@components/ReportList/reportList.types';
import ReportTable from '@components/ReportList/ReportTable/ReportTable';
import { SortedColumn } from '@components/ReportList/types/reportList.types';
import { mapReportToSummary } from '@components/ReportList/utils/mapReportToSummary';
import Octicons from '@expo/vector-icons/Octicons';
import { useAppTranslation } from '@hooks/useAppTranslation';
import { useBreakpoint } from '@hooks/useBreakpoint';
import { useRouter, useSegments } from 'expo-router';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { NativeScrollEvent, NativeSyntheticEvent } from 'react-native';
import {
  ScrollView,
  SizableText,
  Spinner,
  useTheme,
  XStack,
  YStack,
} from 'tamagui';
import { Select } from '../Select/Select';
import { flattenReportPages } from './utils/flattenReportPages';

interface ReportListProps {
  reportPages: Record<number, ReportResponseDto[]>;
  reportCount: number;
  page: number;
  setPage: (page: number) => void;
  sortedColumns: SortedColumn[];
  onSortingChange: (sortedColumns: SortedColumn[]) => void;
  selectedReportIds?: string[];
  onSelectionChange?: (
    selectedReports: ReportResponseDto[],
    currentPageReports: ReportResponseDto[],
  ) => void;
  pageSize?: number;
  onPageSizeChange?: (pageSize: number) => void;
  enableNavigation?: boolean;
  onLoadMore?: () => void;
  hasMore?: boolean;
  isLoadingMore?: boolean;
}

export default function ReportList({
  reportPages,
  reportCount,
  page,
  setPage,
  sortedColumns,
  onSortingChange,
  selectedReportIds,
  onSelectionChange,
  pageSize = REPORTS_PAGE_LIMIT,
  onPageSizeChange,
  enableNavigation = true,
  onLoadMore,
  hasMore,
  isLoadingMore,
}: ReportListProps): React.ReactNode {
  const { i18n } = useTranslation();
  const { t } = useAppTranslation();
  const { isSmallScreen } = useBreakpoint();
  const router = useRouter();
  const segments = useSegments() as string[];
  const theme = useTheme();
  const isBoardRoute = segments.includes('board');
  const reportDetailBase = isBoardRoute ? '/board/reports' : '/reports';
  const isLazyLoadEnabled = isSmallScreen;
  const canLoadMoreReports =
    isLazyLoadEnabled && onLoadMore && hasMore && !isLoadingMore;
  const displayedReports = isLazyLoadEnabled
    ? flattenReportPages(reportPages)
    : (reportPages[page] ?? []);
  const reportsSummary: ReportSummary[] = displayedReports?.map((report) =>
    mapReportToSummary(report, i18n),
  );
  const sortIconColor = theme.secondary75?.val ?? theme.color.val;
  const activeSort = sortedColumns[0];
  const sortColumnId = activeSort?.id ?? '';
  const sortOrder = activeSort?.order ?? 'desc';

  const safePageSize = Math.max(1, pageSize);
  const totalPages = Math.max(1, Math.ceil(reportCount / safePageSize));

  const handleReportPress = enableNavigation
    ? (reportId: string): void => {
        router.push(`${reportDetailBase}/${reportId}`);
      }
    : undefined;

  const handleSelected = (report: ReportSummary): void => {
    if (handleReportPress) {
      handleReportPress(report.id);
    }
  };

  const handleScroll = (
    event: NativeSyntheticEvent<NativeScrollEvent>,
  ): void => {
    if (!canLoadMoreReports) {
      return;
    }

    const { layoutMeasurement, contentOffset, contentSize } = event.nativeEvent;

    const distanceFromBottom =
      contentSize.height - (contentOffset.y + layoutMeasurement.height);
    const loadThreshold = 120;

    if (distanceFromBottom <= loadThreshold) {
      onLoadMore?.();
    }
  };

  const sortOptions = useMemo(() => getReportSortOptions(t), [t]);
  const handleSelectionChange = (selectedReports: ReportSummary[]): void => {
    if (!onSelectionChange) {
      return;
    }

    const currentDisplayedReports = isLazyLoadEnabled
      ? flattenReportPages(reportPages)
      : (reportPages[page] ?? []);
    const reportById = new Map(
      currentDisplayedReports.map((report) => [report.id, report]),
    );
    const selectedReportsFull = selectedReports
      .map((report) => reportById.get(report.id))
      .filter((report): report is ReportResponseDto => Boolean(report));

    onSelectionChange(selectedReportsFull, currentDisplayedReports);
  };

  const handleSortColumnChange = useCallback(
    (value: string): void => {
      if (!value) {
        onSortingChange([]);
        return;
      }

      onSortingChange([{ id: value, order: sortOrder }]);
    },
    [onSortingChange, sortOrder],
  );

  const handleSortOrderToggle = useCallback((): void => {
    if (!sortColumnId) {
      return;
    }

    const nextOrder = sortOrder === 'asc' ? 'desc' : 'asc';
    onSortingChange([{ id: sortColumnId, order: nextOrder }]);
  }, [onSortingChange, sortColumnId, sortOrder]);

  if (!isSmallScreen) {
    return (
      <ScrollView flex={1} showsVerticalScrollIndicator={false}>
        <ReportTable
          reports={reportsSummary}
          selectable={true}
          onReportPress={handleReportPress}
          selectedReportIds={selectedReportIds}
          onSelectionChange={handleSelectionChange}
          onSortingChange={onSortingChange}
        />
        <Pagination
          page={page}
          count={totalPages}
          onChange={setPage}
          pageSize={pageSize}
          pageSizeOptions={REPORTS_PAGE_SIZE_OPTIONS}
          onPageSizeChange={onPageSizeChange}
          pageSizeLabel={t('reports.pageSize')}
        />
      </ScrollView>
    );
  }

  return (
    <ScrollView
      flex={1}
      showsVerticalScrollIndicator={false}
      onScroll={isLazyLoadEnabled ? handleScroll : undefined}
      scrollEventThrottle={isLazyLoadEnabled ? 16 : undefined}
    >
      <YStack gap="$2" paddingBottom="$4">
        <XStack gap="$2" alignItems="center" justifyContent="space-between">
          <SizableText color="$secondary75">
            {t('reports.count', { count: reportCount })}
          </SizableText>
          <XStack gap="$2" alignItems="center">
            <Button
              variant="ghost"
              onPress={handleSortOrderToggle}
              disabled={!sortColumnId}
              accessibilityLabel={t('reports.sort.order')}
              padding="$1"
              icon={
                <Octicons
                  name={sortOrder === 'asc' ? 'sort-asc' : 'sort-desc'}
                  size={20}
                  color={sortIconColor}
                />
              }
            />
            <YStack flex={1}>
              <Select
                options={sortOptions}
                value={sortColumnId}
                onValueChange={handleSortColumnChange}
                placeholder={t('reports.sort.placeholder')}
              />
            </YStack>
          </XStack>
        </XStack>

        {reportsSummary.map((report) => (
          <ReportCard
            key={report.id}
            report={report}
            onPress={() => handleSelected(report)}
          />
        ))}
        {isLoadingMore && (
          <YStack>
            <Spinner size="small"></Spinner>
            <SizableText textAlign="center" color="$secondary50">
              {t('reports.loadingMore')}
            </SizableText>
          </YStack>
        )}
      </YStack>
    </ScrollView>
  );
}
